import { zodResolver } from '@hookform/resolvers/zod';
import { Can, usePermission } from '@smartsash/permissions';
import type { ActionId, PermissionKey } from '@smartsash/permissions';
import { Banner, Button, Table, Typography } from '@smartsash/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { QueryClient, UseSuspenseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable, type SortingState } from '@tanstack/react-table';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';

import { filterRowsByQuery, sortRowsByColumn } from './clientList';
import { tableColumnsFor } from './columns';
import { DATA_SCREEN_MESSAGE_KEYS, DEFAULT_ID_PARAM, DEFAULT_SEARCH_DEBOUNCE_MS } from './config';
import { applyApiFormError } from './errors';
import { toSortSpec, toTableSort } from './search';
import type { ListSearch } from './search';
import type {
  DataScreenAction,
  DataScreenFormProps,
  DataScreenQueryOptions,
  DefineDataScreenConfig,
} from './types';

function suspenseOptions<TData, TError>(
  options: DataScreenQueryOptions<TData, TError>,
): UseSuspenseQueryOptions<TData, TError, TData> {
  return options as unknown as UseSuspenseQueryOptions<TData, TError, TData>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Gate content behind an action only when one is configured (they are optional). */
function Guard({ permission, children }: { permission?: ActionId; children: ReactNode }) {
  return permission == null ? children : <Can action={permission}>{children}</Can>;
}

/**
 * The form's own gate, keyed on a raw catalogue key rather than an action.
 *
 * `config.permissions` is route-guard configuration — `requirePermission` takes a `PermissionKey`
 * — and the submit and delete buttons deliberately read the same entries the guards do, so that a
 * screen cannot offer a write its own route would refuse. That makes this the one place inside
 * the factory where a key, not an action, is the right currency.
 */
function KeyGuard({ permission, children }: { permission?: PermissionKey; children: ReactNode }) {
  const { canKey } = usePermission();
  if (permission == null) return children;
  return canKey(permission) ? children : null;
}

function ActionSlot<TValue>({
  action,
  value,
}: {
  action: DataScreenAction<TValue>;
  value: TValue;
}) {
  return <Guard permission={action.permission}>{action.render(value)}</Guard>;
}

function Actions<TValue>({
  actions,
  value,
}: {
  actions: DataScreenAction<TValue>[];
  value: TValue;
}) {
  return actions.map((action) => (
    <Fragment key={action.id}>
      <ActionSlot action={action} value={value} />
    </Fragment>
  ));
}

export function defineDataScreen<
  TRow,
  TSearch extends ListSearch,
  TId = string,
  TDetail = TRow,
  TValues extends FieldValues = FieldValues,
  TCreateVariables = TValues,
  TUpdateVariables = TValues,
  TDeleteVariables = TId,
  TGuardContext = unknown,
  TListError = Error,
  TDetailError = Error,
>(
  config: DefineDataScreenConfig<
    TRow,
    TSearch,
    TId,
    TDetail,
    TValues,
    TCreateVariables,
    TUpdateVariables,
    TDeleteVariables,
    TGuardContext,
    TListError,
    TDetailError
  >,
) {
  const validateSearch = (input: unknown): TSearch => config.searchSchema.parse(input);

  const searchScope = config.list.searchScope ?? 'server';
  const sortScope = config.list.sortScope ?? 'server';

  /**
   * The search the query sees. A client-scope axis stays in the URL but is dropped here, so a
   * keystroke or a sort click cannot churn the query key — and the loader's deps cannot disagree
   * with the component's key over a parameter the server never receives.
   */
  const toQueryInput = (search: TSearch): TSearch => {
    if (searchScope === 'server' && sortScope === 'server') return search;
    const next: Record<string, unknown> = { ...search };
    if (searchScope === 'client') delete next.q;
    if (sortScope === 'client') delete next.sort;
    return next as TSearch;
  };

  const loaderDeps = ({ search }: { search: TSearch }): TSearch => toQueryInput(search);

  // The route parameter is accepted for symmetry with TanStack's own helpers and for a future
  // typed loader; nothing reads it, so a file route can call `loaderFor()` without referencing
  // its own `Route` const (which TypeScript cannot resolve inside its initializer).
  const loaderFor =
    (_route?: unknown) =>
    async ({ context, deps }: { context: { queryClient: QueryClient }; deps: TSearch }) =>
      context.queryClient.ensureQueryData(suspenseOptions(config.list.query(deps)));

  function ListScreen() {
    const translate = config.useTranslate();
    const routeSearch: unknown = useSearch({ strict: false });
    const search = validateSearch(routeSearch);
    const navigate = useNavigate();
    const [query, setQuery] = useState(search.q ?? '');
    const isEditingQuery = useRef(false);
    const searchable = config.list.searchable ?? true;

    const updateSearch = useCallback(
      (patch: Partial<TSearch>) => {
        void navigate({
          search: { ...search, ...patch } as never,
          replace: true,
        });
      },
      [navigate, search],
    );

    useEffect(() => {
      const next = search.q ?? '';
      if (next === query) {
        isEditingQuery.current = false;
      } else if (!isEditingQuery.current) {
        setQuery(next);
      }
    }, [query, search.q]);

    useEffect(() => {
      // A non-searchable list has no `q` to commit; skipping the timer keeps it from
      // ever writing `q` back into the URL.
      if (!searchable) return undefined;
      const committed = search.q ?? '';
      if (query === committed) return undefined;
      const timeout = window.setTimeout(() => {
        updateSearch({ q: query === '' ? undefined : query, page: 1 } as Partial<TSearch>);
      }, config.list.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS);
      return () => {
        window.clearTimeout(timeout);
      };
    }, [query, search.q, searchable, updateSearch]);

    const result = useSuspenseQuery(suspenseOptions(config.list.query(toQueryInput(search)))).data;
    const sorting = useMemo<SortingState>(
      () =>
        search.sort == null ? [] : [{ id: search.sort.field, desc: search.sort.dir === 'desc' }],
      [search.sort],
    );
    const pageCount = Math.max(1, Math.ceil(result.total / search.pageSize));
    // Joined lookups the columns need (an id → name map, say). The definition is static, so this
    // optional hook is called in the same position on every render.
    const tableMeta = config.list.useTableMeta?.();
    const narrowRows = config.list.narrowRows;
    const items = useMemo(
      () => (narrowRows == null ? result.items : narrowRows(result.items, search)),
      [narrowRows, result.items, search],
    );
    const table = useReactTable({
      data: items,
      columns: config.list.columns,
      getRowId: (row) => config.list.getRowId(row),
      getCoreRowModel: getCoreRowModel(),
      meta: tableMeta,
      manualFiltering: true,
      manualPagination: true,
      manualSorting: true,
      pageCount,
      state: {
        sorting,
        pagination: { pageIndex: search.page - 1, pageSize: search.pageSize },
        globalFilter: search.q ?? '',
      },
    });
    const columns = tableColumnsFor(table.getAllLeafColumns(), table.getFlatHeaders(), tableMeta);
    // `Table` receives `searchValue`/`sort`/`page`, which puts all three axes in controlled mode,
    // so it never narrows or orders on its own. A client-scope axis is therefore narrowed exactly
    // once, here, over the page the server returned.
    const coreRows = table.getRowModel().rows;
    const rows = useMemo(() => {
      const searched =
        searchScope === 'client' ? filterRowsByQuery(coreRows, columns, search.q) : coreRows;
      return sortScope === 'client' ? sortRowsByColumn(searched, columns, search.sort) : searched;
      // `columns` is deliberately not a dependency: it is rebuilt from the table instance on every
      // render, so listing it would defeat the memo. What changes the result is the row set and the
      // two URL axes.
    }, [coreRows, search.q, search.sort]);

    // Pull the URL back when it points past the end: a shared `?page=99`, or the last row on the
    // last page deleted. Without it the screen shows a pager over nothing (admin's HS-1151 fix).
    useEffect(() => {
      if (search.page > pageCount) updateSearch({ page: pageCount } as Partial<TSearch>);
    }, [pageCount, search.page, updateSearch]);

    /**
     * "Nothing here yet" and "nothing matches what you asked for" are different situations with
     * different fixes, so an empty page caused by a query says so. `filters` is the one search key
     * the builder writes; a screen that declares no filtered copy keeps its single message.
     */
    const emptyState = useMemo(() => {
      const configured = config.list.emptyState;
      const empty = typeof configured === 'function' ? configured(search) : configured;
      const activeFilters = search.filters;
      const isNarrowed =
        (search.q != null && search.q !== '') ||
        (Array.isArray(activeFilters) ? activeFilters.length > 0 : activeFilters != null);

      const titleKey = (isNarrowed ? empty.filteredTitleKey : undefined) ?? empty.titleKey;
      const descriptionKey =
        (isNarrowed ? empty.filteredDescriptionKey : undefined) ?? empty.descriptionKey;

      return {
        title: translate(titleKey),
        description: descriptionKey == null ? undefined : translate(descriptionKey),
        icon: empty.icon,
        actions: empty.actions,
      };
    }, [search, translate]);

    const rowActions = config.list.rowActions;
    const mobileRowActions = config.list.mobileRowActions;
    const headerAction = config.list.headerAction;
    const mobileHeaderAction = config.list.mobileHeaderAction;

    return (
      <Table
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        title={translate(config.list.titleKey)}
        badge={result.total}
        titleAddon={config.list.titleAddon?.(search)}
        cardsBelowWidth={config.list.cardsBelowWidth}
        cardLayout={config.list.cardLayout}
        searchable={searchable}
        searchPlaceholder={
          config.list.searchPlaceholderKey == null
            ? undefined
            : translate(config.list.searchPlaceholderKey)
        }
        searchValue={query}
        onSearchChange={(value) => {
          isEditingQuery.current = true;
          setQuery(value);
        }}
        selectable={config.list.selectable ?? false}
        onSelectionChange={config.list.onSelectionChange}
        sort={toTableSort(search.sort)}
        onSortChange={(nextSort) => {
          updateSearch({
            sort: toSortSpec(nextSort.columnId, nextSort.direction),
            page: 1,
          } as Partial<TSearch>);
        }}
        pageSize={search.pageSize}
        page={search.page}
        totalPages={pageCount}
        onPageChange={(page) => {
          updateSearch({ page } as Partial<TSearch>);
        }}
        renderCard={(row, context) => config.list.mobileCard(row.original, context)}
        rowActions={
          rowActions == null
            ? undefined
            : (row) => <Actions actions={rowActions} value={row.original} />
        }
        mobileRowActions={
          mobileRowActions == null
            ? undefined
            : (row) => <Actions actions={mobileRowActions} value={row.original} />
        }
        headerAction={
          headerAction == null ? undefined : <ActionSlot action={headerAction} value={search} />
        }
        mobileHeaderAction={
          mobileHeaderAction == null ? undefined : (
            <ActionSlot action={mobileHeaderAction} value={search} />
          )
        }
        filterSlot={config.list.filterSlot?.(search)}
        notice={config.list.notice?.(search)}
        emptyState={emptyState}
        onRowClick={
          config.list.onRowClick == null
            ? undefined
            : (row) => {
                config.list.onRowClick?.(row.original);
              }
        }
      />
    );
  }

  function DetailScreen() {
    const translate = config.useTranslate();
    const params: unknown = useParams({ strict: false });
    const detailConfig = config.detail;
    if (detailConfig == null) {
      throw new Error(`data-screen: "${config.entity}" defines no detail screen`);
    }
    const idParam = detailConfig.idParam ?? DEFAULT_ID_PARAM;
    const rawId = isRecord(params) ? params[idParam] : undefined;
    if (typeof rawId !== 'string' || rawId === '') {
      throw new Error(`Missing route parameter: ${idParam}`);
    }
    const id =
      detailConfig.parseId == null ? (rawId as unknown as TId) : detailConfig.parseId(rawId);
    const detail = useSuspenseQuery(suspenseOptions(detailConfig.query(id))).data;

    return (
      <section>
        {detailConfig.titleKey == null ? null : (
          <Typography as="h1" variant="display-xs" weight="semibold">
            {translate(detailConfig.titleKey)}
          </Typography>
        )}
        {detailConfig.render(detail, translate)}
      </section>
    );
  }

  function FormScreen({
    mode,
    id,
    defaultValues,
    onSuccess,
    onDeleteSuccess,
  }: DataScreenFormProps<TValues, TId>) {
    const translate = config.useTranslate();
    const formConfig = config.form;
    if (formConfig == null) {
      throw new Error(`data-screen: "${config.entity}" defines no form screen`);
    }
    const createMutation = formConfig.create.useMutation();
    const updateMutation = formConfig.update.useMutation();
    const deleteMutation = formConfig.delete.useMutation();
    const form = useForm<TValues, unknown, TValues>({
      resolver: zodResolver(formConfig.schema),
      defaultValues: defaultValues ?? formConfig.defaultValues,
    });
    const rootError = form.formState.errors.root?.server?.message;
    const pending = createMutation.isPending || updateMutation.isPending;

    const submit = form.handleSubmit(async (values) => {
      try {
        if (mode === 'create') {
          await createMutation.mutateAsync(formConfig.create.toVariables(values, { id }));
        } else {
          await updateMutation.mutateAsync(formConfig.update.toVariables(values, { id }));
        }
        onSuccess?.();
      } catch (error) {
        applyApiFormError(error, form.setError, translate);
      }
    });

    const remove = async () => {
      try {
        await deleteMutation.mutateAsync(formConfig.delete.toVariables(form.getValues(), { id }));
        onDeleteSuccess?.();
      } catch (error) {
        applyApiFormError(error, form.setError, translate);
      }
    };

    const submitPermission =
      mode === 'create' ? config.permissions?.create : config.permissions?.edit;

    return (
      <section>
        <Typography as="h1" variant="display-xs" weight="semibold">
          {translate(formConfig.titleKeys[mode])}
        </Typography>
        {rootError == null ? null : <Banner type="error">{rootError}</Banner>}
        <form
          onSubmit={(event) => {
            void submit(event);
          }}
          noValidate
        >
          {formConfig.render({ form, mode, translate })}
          <KeyGuard permission={submitPermission}>
            <Button type="submit" loading={pending}>
              {translate(formConfig.submitKey ?? DATA_SCREEN_MESSAGE_KEYS.save)}
            </Button>
          </KeyGuard>
          {mode === 'edit' ? (
            <KeyGuard permission={config.permissions?.delete}>
              <Button
                type="button"
                hierarchy="destructive"
                loading={deleteMutation.isPending}
                onClick={() => {
                  void remove();
                }}
              >
                {translate(formConfig.deleteKey ?? DATA_SCREEN_MESSAGE_KEYS.delete)}
              </Button>
            </KeyGuard>
          ) : null}
        </form>
      </section>
    );
  }

  // A guard exists only where both a key and a `requirePermission` factory do. An app that has
  // not wired route permissions yet (admin, HS-803) gets `undefined` and wires nothing.
  const guardFor = (key: PermissionKey | undefined) => {
    const requirePermission = config.permissions?.requirePermission;
    return requirePermission == null || key == null ? undefined : requirePermission(key);
  };

  return {
    entity: config.entity,
    validateSearch,
    loaderDeps,
    loaderFor,
    beforeLoad: {
      list: guardFor(config.permissions?.view),
      detail: guardFor(config.permissions?.view),
      create: guardFor(config.permissions?.create),
      edit: guardFor(config.permissions?.edit),
    },
    ListScreen,
    DetailScreen,
    FormScreen,
    columns: config.list.columns,
  };
}
